import React from "react";

import { Form, Date } from "@weezevent/nacre";
import { useController, useFormContext } from "react-hook-form";
import PropTypes from "prop-types";

import moment, { isMoment } from "moment/moment";
import useFormPrefix from "../hooks/useFormPrefix";
import { useTranslation } from "react-i18next";
import classes from "./FormFieldDateTimeInput.module.scss";

export const dateToMoment = date => {
	if (isMoment(date)) {
		return date;
	}

	return moment(date);
};

export const updateHourFromString = (date, hour) => {
	const [hourValue, minuteValue] = hour.split(":");

	const updatedDate = date ? dateToMoment(date).clone() : moment();

	updatedDate.set("hours", hourValue);
	updatedDate.set("minutes", minuteValue);

	return updatedDate;
};

export const formatHourValue = date => {
	if (date) {
		return dateToMoment(date).format("HH:mm");
	}

	return undefined;
};

export const ControlledDateTime = ({
	value,
	defaultHour,
	onChange,
	label,
	hasError,
	minDate,
	dateOnly
}) => {
	const { i18n } = useTranslation();
	const locale = i18n.accountLocale;

	const onDateChange = React.useCallback(
		date => {
			if (date) {
				// Apply the minimum date limit if specified
				if (minDate && date.isBefore(minDate)) {
					date = minDate.clone();
				}
				if (value) {
					const momentValue = dateToMoment(value);
					date.set("hours", momentValue.hour());
					date.set("minutes", momentValue.minute());

					onChange(date);
				} else {
					onChange(updateHourFromString(date, defaultHour));
				}
			} else {
				onChange(null);
			}
		},
		[onChange, defaultHour, value, minDate]
	);

	const onHourChange = React.useCallback(
		hour => onChange(updateHourFromString(value, hour)),
		[value, onChange]
	);
	const currrentDateISO = value ? value.toISOString() : null;

	return (
		<Form.Field>
			{!!label && <Form.Label>{label}</Form.Label>}
			<div className={classes.inputs}>
				<Date.Input
					className={classes.date}
					locale={locale}
					value={currrentDateISO}
					onChange={onDateChange}
					errors={hasError}
					minDate={minDate}
				/>
				{!dateOnly && (
					<Form.Time
						className={classes.time}
						value={formatHourValue(value)}
						placeholder={defaultHour}
						onChange={onHourChange}
						step={5}
						errors={hasError}
					/>
				)}
			</div>
		</Form.Field>
	);
};

ControlledDateTime.propTypes = {
	value: PropTypes.object,
	/**
	 * The default hour of the field.
	 * Used by react-hook-form.
	 */
	defaultHour: PropTypes.string,
	/**
	 * The function to call when the field changes.
	 */
	onChange: PropTypes.func.isRequired,
	/**
	 * The label of the field to display.
	 */
	label: PropTypes.node,
	/**
	 * If the field has an error.
	 */
	hasError: PropTypes.bool,
	/**
	 * force start date to be minDate
	 */
	minDate: PropTypes.string,
	/**
	 * specifies if we only want to show the date
	 */
	dateOnly: PropTypes.bool
};

ControlledDateTime.defaultProps = {
	value: null,
	defaultHour: "12:00",
	label: undefined,
	hasError: false,
	minDate: null,
	dateOnly: false
};

/**
 * This component is an input to manage a select with multiple options.
 * It's a controlled field that deals with react-hook-form. Must be wrapped by a FormContext.
 */
const FormFieldDate = ({
	name,
	rules,
	defaultDate,
	defaultHour,
	onChange: onChangeAdditional,
	label,
	minDate,
	control: controlProp,
	...props
}) => {
	const prefix = useFormPrefix();
	// Get control from context if not provided as prop
	const formContext = useFormContext();
	const control = controlProp || (formContext && formContext.control);

	const {
		field: { onChange, value },
		fieldState: { error }
	} = useController({
		name: prefix ? `${prefix}.${name}` : name,
		rules,
		defaultValue: defaultDate
			? updateHourFromString(moment(defaultDate), defaultHour)
			: defaultDate,
		control
	});

	const onChangeValue = React.useCallback(
		v => {
			onChange(v);
			if (onChangeAdditional) {
				onChangeAdditional(v);
			}
		},
		[onChange, onChangeAdditional]
	);

	return (
		<ControlledDateTime
			name={name}
			value={value}
			onChange={onChangeValue}
			defaultHour={defaultHour}
			label={label}
			hasError={!!error}
			minDate={minDate} // Passe `minDate` pour désactiver les dates antérieures
			{...props}
		/>
	);
};

FormFieldDate.propTypes = {
	/**
	 * The name of the field. This name must be unique in the form.
	 * Used by react-hook-form.
	 */
	name: PropTypes.string.isRequired,
	/**
	 * The default date of the field.
	 * Used by react-hook-form.
	 */
	defaultDate: PropTypes.string,
	/**
	 * The default hour of the field.
	 * Used by react-hook-form.
	 */
	defaultHour: PropTypes.string,
	/**
	 * The function to call when the field changes.
	 */
	onChange: PropTypes.func,
	/**
	 * The object of rules to apply to validate the field.
	 * Used by react-hook-form.
	 */
	rules: PropTypes.object,
	/**
	 * The label of the field to display.
	 */
	label: PropTypes.node,
	/**
	 * force start date to be minDate
	 */
	minDate: PropTypes.string,
	/**
	 * The control object from react-hook-form.
	 */
	control: PropTypes.object
};

FormFieldDate.defaultProps = {
	defaultDate: undefined,
	defaultHour: "12:00",
	onChange: null,
	rules: {},
	label: undefined,
	minDate: null,
	control: undefined
};

export default FormFieldDate;
